'use client'

import { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'

const memberAvatars = ['/images/members/member1.jpg', '/images/members/member2.jpg', '/images/members/member3.jpg', '/images/members/member4.jpg']

interface NewsletterSignupProps {
  source?: string
  variant?: 'hero' | 'inline' | 'bottom'
}

export default function NewsletterSignup({ source = 'blog', variant = 'inline' }: NewsletterSignupProps) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setStatus('loading')
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source }),
      })
      if (res.ok) {
        setStatus('success')
        setEmail('')

        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', {
            content_name: 'newsletter_signup',
            content_category: source,
          })
        }

        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            send_to: 'AW-17984603839/20ewCKLx7pkcEL-N3f9C',
            value: 1.0,
            currency: 'AUD',
          })
        }
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  if (variant === 'hero') {
    if (status === 'success') {
      return (
        <div className="py-4">
          <div className="inline-flex items-center gap-2 text-green-400 font-medium">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            You&apos;re in! Check your inbox for your first issue.
          </div>
        </div>
      )
    }
    return (
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          required
          className="flex-1 bg-white/10 border border-white/20 rounded-full px-5 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white/40 transition-colors text-sm"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="bg-white text-black font-semibold px-6 py-3 rounded-full text-sm hover:bg-gray-200 transition-colors disabled:opacity-50 shrink-0"
        >
          {status === 'loading' ? 'Subscribing...' : 'Subscribe for free'}
        </button>
        {status === 'error' && (
          <p className="text-red-400 text-xs mt-1">Something went wrong. Try again.</p>
        )}
      </form>
    )
  }

  if (variant === 'inline') {
    if (status === 'success') {
      return (
        <div className="rounded-2xl border border-green-500/20 bg-green-500/5 px-6 py-6 text-center">
          <p className="text-green-400 font-medium">Subscribed! Check your inbox.</p>
        </div>
      )
    }
    return (
      <div className="rounded-2xl border border-gray-800 bg-[#0a0a0a] px-6 py-6 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-white mb-1">Free AI Builder Newsletter</h3>
            <p className="text-sm text-gray-400">Weekly guides on AI tools &amp; builder strategies.</p>
          </div>
          <form onSubmit={handleSubmit} className="flex gap-2 shrink-0">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              required
              className="bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-white/40 transition-colors w-44"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="border border-white/20 text-white font-medium px-4 py-2 rounded-full text-sm hover:bg-white/10 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {status === 'loading' ? '...' : 'Subscribe'}
            </button>
          </form>
        </div>
        {status === 'error' && (
          <p className="text-red-400 text-xs mt-3">Something went wrong. Try again.</p>
        )}
      </div>
    )
  }

  // bottom variant — paid membership + free newsletter
  return (
    <div className="space-y-6">
      {/* Paid membership (primary revenue CTA) */}
      <div className="relative rounded-2xl p-[1px] bg-gradient-to-r from-blue-500/40 via-purple-500/40 to-pink-500/40">
        <div className="relative bg-[#0a0a0a] rounded-2xl overflow-hidden">
          <div className="grid lg:grid-cols-2 items-stretch">
            {/* Left: copy + CTA */}
            <div className="p-8 sm:p-10 flex flex-col justify-center">
              <h3 className="text-2xl sm:text-[28px] font-bold text-white leading-tight mb-5">
                Join AI Builder Club
              </h3>
              <div className="space-y-2.5 mb-7 text-sm text-gray-300">
                <div className="flex items-center gap-2.5">
                  <span className="text-blue-400 text-base leading-5">&#10003;</span>
                  65+ lessons, 22+ workshops
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-blue-400 text-base leading-5">&#10003;</span>
                  350+ plug-and-play prompts &amp; skills
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-blue-400 text-base leading-5">&#10003;</span>
                  Weekly live builder workshop
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-blue-400 text-base leading-5">&#10003;</span>
                  Premium tools (e.g. 10xCoder, AI tutor)
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-blue-400 text-base leading-5">&#10003;</span>
                  AI Builder Pack ($5,000+ in exclusive AI credits &amp; perks)
                </div>
              </div>
              {/* Social proof glued to the CTA */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex -space-x-2.5">
                  {memberAvatars.map((src, i) => (
                    <Image
                      key={src}
                      src={src}
                      alt=""
                      width={32}
                      height={32}
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-[#0a0a0a]"
                      style={{ zIndex: memberAvatars.length - i }}
                    />
                  ))}
                  <div className="w-8 h-8 rounded-full bg-white/10 ring-2 ring-[#0a0a0a] flex items-center justify-center text-[10px] font-semibold text-white">
                    1k+
                  </div>
                </div>
                <span className="text-xs text-gray-400">
                  Join <span className="text-white font-medium">1,000+ builders</span> already inside
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <Link
                  href="/pricing"
                  className="bg-white text-black font-semibold px-6 py-2.5 rounded-full text-sm hover:bg-gray-200 transition-colors"
                >
                  Start shipping →
                </Link>
                <span className="text-xs text-gray-400">30-day money-back · Cancel anytime</span>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                <span className="text-gray-300 font-medium">$37/mo</span>
              </p>
            </div>

            {/* Right: live community call */}
            <div className="relative hidden lg:block overflow-hidden">
              <div className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-r from-[#0a0a0a] via-transparent to-transparent" />
              <video
                className="absolute top-1/2 -translate-y-1/2 left-8 w-[125%] max-w-none rounded-xl shadow-2xl ring-1 ring-white/10"
                autoPlay
                loop
                muted
                playsInline
                poster="/videos/community-call-poster.jpg"
              >
                <source src="/videos/community-call.webm" type="video/webm" />
                <source src="/videos/community-call.mp4" type="video/mp4" />
              </video>
              <div className="absolute top-6 left-8 z-20 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm px-2.5 py-1 rounded-full ring-1 ring-white/10">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                </span>
                <span className="text-[11px] font-medium text-white">Live workshop</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Free newsletter (soft capture) */}
      <div className="rounded-2xl border border-gray-800 bg-[#0a0a0a] p-8 sm:p-10 text-center">
        {status === 'success' ? (
          <div className="py-2">
            <div className="inline-flex items-center gap-2 text-green-400 font-medium mb-3">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              You&apos;re subscribed!
            </div>
            <p className="text-gray-400 text-sm">Check your inbox for your first issue.</p>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-bold text-white mb-2">
              Get the free newsletter
            </h3>
            <p className="text-gray-400 text-sm mb-5 max-w-md mx-auto">
              Weekly deep-dives on AI tools, automation workflows, and builder strategies. 
              Join 5,000+ readers.
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                className="flex-1 bg-white/10 border border-white/20 rounded-full px-5 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white/40 transition-colors text-sm"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="border border-white/20 text-white font-semibold px-6 py-3 rounded-full text-sm hover:bg-white/10 transition-colors disabled:opacity-50 shrink-0"
              >
                {status === 'loading' ? 'Subscribing...' : 'Subscribe for free'}
              </button>
            </form>
            <p className="text-xs text-gray-500">No spam. Unsubscribe anytime.</p>
            {status === 'error' && (
              <p className="text-red-400 text-xs mt-2">Something went wrong. Try again.</p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
